import React from "react";
import MaskReveal from "../../shared/components/UIElements/MaskReveal";
import Reveal from "../../shared/components/UIElements/Reveal";
import OperationalDataDemo from "../components/OperationalDataDemo";
import MaintenanceDemo from "../components/MaintenanceDemo";
import "./styling/OperationsSection.css";

const DATA_PILLS = [
  "Define Your Own Columns",
  "Any Unit You Use",
  "Excel & CSV Import",
  "Shift-Level Entries",
  "Inline Cell Editing",
  "Chart Any Series",
];

const MAINT_PILLS = [
  "Facility → Area → Device → Part",
  "Problem / Repair / Replacement",
  "Downtime & Cost Capture",
  "Rolled-up Open Issues",
  "Sub-asset History",
];

/**
 * OperationsSection — homepage section for the two Operation pages
 * (Operational Data and Maintenance Logs). Both get a live preview driven by
 * illustrative data rather than a screenshot, so the interactions that define
 * them — switching logs, plotting a column, drilling into an asset — are
 * actually demonstrable on the marketing page.
 */
export default function OperationsSection() {
  return (
    <section className="ops-section" aria-labelledby="operations-heading">
      <MaskReveal as="h2" id="operations-heading" className="section-heading">
        The Site Record, Alongside the Model
      </MaskReveal>

      <Reveal as="p" className="ops-section__intro">
        A forecast is only as good as the operation it is measured against.
        Orecasting keeps the day-to-day site record — what was stacked, what was
        pumped, what broke and what it cost — in the same project as the
        simulations, so the evidence and the model never live in different
        spreadsheets.
      </Reveal>

      <div className="ops-section__block">
        <Reveal className="ops-section__copy">
          <span className="ops-section__eyebrow">Operational Data</span>
          <h3>Replace the site spreadsheet</h3>
          <p>
            Build a log that matches how your operation actually records things.
            Add the columns you need, name them, give each one its own unit and
            precision, and choose whether you are logging once a day or once a
            shift. Type straight into the table, or import years of history from
            Excel — headers like <em>PLS Flow (m³/h)</em> are split into a label
            and a unit automatically, and column types are detected from the data.
          </p>
          <p>
            Every entry is stored against a date key, the same key simulation
            results are written on — so the record you are building today is
            already structured for comparing actuals against the model.
          </p>
          <ul className="ops-section__pills">
            {DATA_PILLS.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal className="ops-section__demo" delay={90}>
          <OperationalDataDemo />
        </Reveal>
      </div>

      <div className="ops-section__block ops-section__block--reverse">
        <Reveal className="ops-section__copy">
          <span className="ops-section__eyebrow">Maintenance Logs</span>
          <h3>Every asset, and everything that happened to it</h3>
          <p>
            Describe the plant the way it is actually laid out — facilities broken
            into areas, areas into devices, devices into individual parts — and
            skip any level that doesn't apply. Log problems, repairs,
            replacements, inspections and routine maintenance against the exact
            asset involved, with the downtime, cost and parts used.
          </p>
          <p>
            Open issues roll up the tree, so a collapsed branch still tells you
            something needs attention. Select any asset to read its history on its
            own, or with everything beneath it included.
          </p>
          <ul className="ops-section__pills">
            {MAINT_PILLS.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal className="ops-section__demo" delay={90}>
          <MaintenanceDemo />
        </Reveal>
      </div>
    </section>
  );
}
